import { useState, type FormEvent, type ReactNode } from 'react'
import type {
  PeriodReport,
  ReportBreakdownRow,
  ReportDayRow,
  CloudShiftDetail
} from '@billiard/shared'
import { useAuth } from '@/auth/AuthContext'
import { useFetch, useLazyFetch } from '@/hooks/useApi'
import { AsyncState, Button, EmptyState, Field, Input, SectionHead, Select } from '@/components/ui'
import {
  money,
  count,
  share,
  duration,
  dateTime,
  dateOnly,
  paymentLabel,
  dateInputToMs,
  msToDateInput
} from '@/lib/format'
import { Cur, Money } from '@/lib/currency'

/** Минимальный контракт списка клубов для селектора super_admin (GET /v1/clubs). */
interface ClubSummary {
  id: string
  name: string
}

const DAY_MS = 24 * 60 * 60 * 1000

export function Reports(): React.JSX.Element {
  return (
    <>
      <div className="eyebrow">Аналитика</div>
      <h2 className="page-title">Отчёты</h2>
      <p className="page-lead">
        Сводка за период с разбивкой по тарифам, столам и операторам и детализацией смены (X/Z). Все
        суммы считаются на бэкенде из неизменяемых фактов смен.
      </p>

      <PeriodTool />
      <CleaningTool />
      <ShiftTool />
    </>
  )
}

// ═══════════════════════════ Уборка столов ═══════════════════════════

/** Ответ GET /v1/reports/cleanings. */
interface CleaningRow {
  key: string
  name: string
  total: number
  done: number
  avgMinutes: number | null
}

interface CleaningReport {
  total: number
  done: number
  pending: number
  avgMinutes: number | null
  medianMinutes: number | null
  maxMinutes: number | null
  byTable: CleaningRow[]
  byStaff: CleaningRow[]
}

/**
 * Сколько стол простаивает между гостями и кто как быстро убирает.
 * Отдельный инструмент, а не блок отчёта за период: смотрят его не вместе с
 * выручкой, а когда разбираются с очередью на столы.
 */
function CleaningTool(): React.JSX.Element {
  const { isSuperAdmin } = useAuth()
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const [clubId, setClubId] = useState('')
  const clubs = useFetch<ClubSummary[]>(isSuperAdmin ? '/v1/clubs' : null)
  const report = useLazyFetch<CleaningReport>()

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    void report.run('/v1/reports/cleanings', {
      from: dateInputToMs(fromStr),
      to: dateInputToMs(toStr) + DAY_MS - 1,
      clubId: isSuperAdmin && clubId ? clubId : undefined
    })
  }

  const mins = (v: number | null): string => (v == null ? '—' : `${v} мин`)

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead
        title="Уборка столов"
        meta="Задачи заводятся на кассе после каждой игры (настройка терминала)"
      />

      <form className="report-form" onSubmit={onSubmit}>
        <Field label="С даты" htmlFor="clean-from">
          <Input
            id="clean-from"
            type="date"
            value={fromStr}
            max={toStr}
            onChange={(e) => setFromStr(e.target.value)}
            required
          />
        </Field>
        <Field label="По дату" htmlFor="clean-to">
          <Input
            id="clean-to"
            type="date"
            value={toStr}
            min={fromStr}
            onChange={(e) => setToStr(e.target.value)}
            required
          />
        </Field>
        {isSuperAdmin && (
          <div className="field field--grow">
            <label className="field__label" htmlFor="clean-club">
              Клуб
            </label>
            {clubs.data && clubs.data.length > 0 ? (
              <Select
                id="clean-club"
                value={clubId}
                onChange={(e) => setClubId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Выберите клуб…
                </option>
                {clubs.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id="clean-club"
                type="text"
                placeholder="UUID клуба"
                value={clubId}
                onChange={(e) => setClubId(e.target.value)}
                required
              />
            )}
          </div>
        )}
        <div className="field field--action">
          <Button
            variant="primary"
            type="submit"
            disabled={report.loading || (isSuperAdmin && !clubId)}
          >
            {report.loading ? <span className="spin" /> : null}
            Построить
          </Button>
        </div>
      </form>

      <div style={{ marginTop: 20 }}>
        <AsyncState query={report} loadingLabel="Считаем уборки…" />
        {report.data && report.data.total === 0 && (
          <EmptyState message="За период задач на уборку не было. Проверьте, включены ли они в настройках терминала." />
        )}
        {report.data && report.data.total > 0 && (
          <>
            <div className="kpi-grid">
              <KpiCompact label="Задач" value={count(report.data.total)} />
              <KpiCompact label="Закрыто" value={count(report.data.done)} />
              <KpiCompact label="Не закрыто" value={count(report.data.pending)} />
              <KpiCompact label="Среднее время" value={mins(report.data.avgMinutes)} gold />
              <KpiCompact label="Медиана" value={mins(report.data.medianMinutes)} />
              <KpiCompact label="Дольше всего" value={mins(report.data.maxMinutes)} />
            </div>
            <div className="grid" style={{ marginTop: 20 }}>
              <CleaningBreakdown title="По столам" rows={report.data.byTable} />
              <CleaningBreakdown title="По сотрудникам" rows={report.data.byStaff} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function CleaningBreakdown({
  title,
  rows
}: {
  title: string
  rows: CleaningRow[]
}): React.JSX.Element {
  return (
    <div>
      <SectionHead title={title} />
      {rows.length === 0 ? (
        <EmptyState message="Нет данных." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Задач</th>
                <th className="num">Закрыто</th>
                <th className="num">Среднее</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  <td className="num">{count(r.total)}</td>
                  <td className="num">{count(r.done)}</td>
                  <td className="num">{r.avgMinutes == null ? '—' : `${r.avgMinutes} мин`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════ Отчёт за период ═══════════════════════════

function PeriodTool(): React.JSX.Element {
  const { isSuperAdmin } = useAuth()

  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const [clubId, setClubId] = useState('')

  // Список клубов для селектора (только super_admin).
  const clubs = useFetch<ClubSummary[]>(isSuperAdmin ? '/v1/clubs' : null)

  const report = useLazyFetch<PeriodReport>()

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    const from = dateInputToMs(fromStr)
    // конец дня «по» включительно
    const to = dateInputToMs(toStr) + DAY_MS - 1
    void report.run('/v1/reports/period', {
      from,
      to,
      clubId: isSuperAdmin && clubId ? clubId : undefined
    })
  }

  const totals = report.data?.totals

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead title="Отчёт за период" />

      <form className="report-form" onSubmit={onSubmit}>
        <Field label="С даты" htmlFor="rep-from">
          <Input
            id="rep-from"
            type="date"
            value={fromStr}
            max={toStr}
            onChange={(e) => setFromStr(e.target.value)}
            required
          />
        </Field>
        <Field label="По дату" htmlFor="rep-to">
          <Input
            id="rep-to"
            type="date"
            value={toStr}
            min={fromStr}
            onChange={(e) => setToStr(e.target.value)}
            required
          />
        </Field>

        {isSuperAdmin && (
          <div className="field field--grow">
            <label className="field__label" htmlFor="rep-club">
              Клуб
            </label>
            {clubs.data && clubs.data.length > 0 ? (
              <Select
                id="rep-club"
                value={clubId}
                onChange={(e) => setClubId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Выберите клуб…
                </option>
                {clubs.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id="rep-club"
                type="text"
                placeholder="UUID клуба"
                value={clubId}
                onChange={(e) => setClubId(e.target.value)}
                required
              />
            )}
          </div>
        )}

        <div className="field field--action">
          <Button
            variant="primary"
            type="submit"
            disabled={report.loading || (isSuperAdmin && !clubId)}
          >
            {report.loading ? <span className="spin" /> : null}
            Построить
          </Button>
        </div>
      </form>

      {isSuperAdmin && clubs.error && (
        <p className="muted" style={{ marginTop: 10, fontSize: 12 }}>
          Список клубов недоступен ({clubs.error}). Введите UUID клуба вручную.
        </p>
      )}

      <div style={{ marginTop: 20 }}>
        <AsyncState query={report} loadingLabel="Считаем отчёт…" />
        {report.data && totals && (
          <>
            <SectionHead
              title="Итоги"
              meta={`${dateOnly(report.data.from)} — ${dateOnly(report.data.to)}`}
            />
            <div className="kpi-grid">
              <KpiCompact label="Выручка" value={<Money value={totals.revenue} />} gold />
              <KpiCompact label="Наличные" value={<Money value={totals.cash} />} />
              <KpiCompact label="Безналичные" value={<Money value={totals.card} />} />
              <KpiCompact label="Сессий" value={count(totals.count)} />
              <KpiCompact label="Скидки" value={<Money value={totals.discount} />} />
              <KpiCompact label="Бонусы списано" value={<Money value={totals.bonusUsed} />} />
            </div>

            <div className="grid" style={{ marginTop: 20 }}>
              <BreakdownTable title="По тарифам" rows={report.data.byTariff} />
              <BreakdownTable title="По столам" rows={report.data.byTable} />
              <BreakdownTable title="По операторам" rows={report.data.byOperator} />
            </div>

            <SectionHead title="По дням" />
            <DayBars rows={report.data.byDay} />
          </>
        )}
      </div>
    </div>
  )
}

function KpiCompact({
  label,
  value,
  gold
}: {
  label: string
  value: ReactNode
  gold?: boolean
}): React.JSX.Element {
  return (
    <div className="kpi">
      <div className="kpi__label">{label}</div>
      <div className={`kpi__value${gold ? ' kpi__value--gold' : ''}`}>{value}</div>
    </div>
  )
}

function BreakdownTable({
  title,
  rows
}: {
  title: string
  rows: ReportBreakdownRow[]
}): React.JSX.Element {
  const total = rows.reduce((acc, r) => acc + r.amount, 0)
  return (
    <div>
      <SectionHead title={title} meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет данных за период." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Наименование</th>
                <th className="num">Кол-во</th>
                <th className="num">Сумма</th>
                <th className="num">Доля</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="num">{count(r.count)}</td>
                  <td className="num">
                    {money(r.amount)} <Cur />
                  </td>
                  <td className="num">{share(r.amount, total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Итого</td>
                <td className="num">{count(rows.reduce((a, r) => a + r.count, 0))}</td>
                <td className="num">
                  {money(total)} <Cur />
                </td>
                <td className="num">100 %</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

function DayBars({ rows }: { rows: ReportDayRow[] }): React.JSX.Element {
  if (rows.length === 0) return <EmptyState message="Нет данных по дням." />
  const max = rows.reduce((m, r) => Math.max(m, r.amount), 0) || 1
  return (
    <div className="bars">
      {rows.map((r) => (
        <div className="bar-row" key={r.day}>
          <span className="bar-row__day">{r.day}</span>
          <span className="bar-row__track">
            <span
              className="bar-row__fill"
              style={{ width: `${Math.max(2, (r.amount / max) * 100)}%` }}
            />
          </span>
          <span className="bar-row__val">
            {money(r.amount)} <Cur />
          </span>
        </div>
      ))}
    </div>
  )
}

// ═══════════════════════════ Смена (X/Z) ═══════════════════════════

function ShiftTool(): React.JSX.Element {
  const [shiftId, setShiftId] = useState('')
  const detail = useLazyFetch<CloudShiftDetail>()

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    const id = shiftId.trim()
    if (id) void detail.run(`/v1/reports/shifts/${encodeURIComponent(id)}`)
  }

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <SectionHead title="Смена (X/Z)" />

      <form className="report-form" onSubmit={onSubmit}>
        <div className="field field--grow">
          <label className="field__label" htmlFor="shift-id">
            ID смены (UUID)
          </label>
          <Input
            id="shift-id"
            type="text"
            placeholder="напр. 0192f1a0-…"
            value={shiftId}
            onChange={(e) => setShiftId(e.target.value)}
            required
          />
        </div>
        <div className="field field--action">
          <Button variant="primary" type="submit" disabled={detail.loading || !shiftId.trim()}>
            {detail.loading ? <span className="spin" /> : null}
            Открыть
          </Button>
        </div>
      </form>

      <div style={{ marginTop: 20 }}>
        <AsyncState query={detail} loadingLabel="Загружаем смену…" />
        {detail.data && <ShiftView detail={detail.data} />}
      </div>
    </div>
  )
}

function ShiftView({ detail }: { detail: CloudShiftDetail }): React.JSX.Element {
  const { shift, totals, sessions, byTariff, byTable, openedByName, closedByName } = detail
  const isOpen = shift.closedAt == null

  return (
    <>
      <SectionHead
        title={`Смена №${shift.number}`}
        meta={
          isOpen ? (
            <span className="pill pill--open">Открыта · X-отчёт</span>
          ) : (
            <span className="pill pill--closed">Закрыта · Z-отчёт</span>
          )
        }
      />

      <div className="kv-grid">
        <KV k="Открыта" v={dateTime(shift.openedAt)} />
        <KV k="Закрыта" v={dateTime(shift.closedAt)} />
        <KV k="Открыл" v={openedByName ?? '—'} />
        <KV k="Закрыл" v={closedByName ?? '—'} />
        <KV k="Касса на начало" v={<Money value={shift.openingCash} />} />
        <KV k="Инкассация" v={<Money value={shift.encashment} />} />
      </div>

      <SectionHead title="Итоги смены" />
      <div className="kpi-grid">
        <KpiCompact label="Выручка" value={<Money value={totals.revenue} />} gold />
        <KpiCompact label="Наличные" value={<Money value={totals.cash} />} />
        <KpiCompact label="Безналичные" value={<Money value={totals.card} />} />
        <KpiCompact label="Сессий" value={count(totals.count)} />
        <KpiCompact label="Скидки" value={<Money value={totals.totalDiscount} />} />
        <KpiCompact label="Бонусы списано" value={<Money value={totals.totalBonusUsed} />} />
      </div>

      <div className="grid" style={{ marginTop: 20 }}>
        <ShiftBreakdown title="По тарифам" rows={byTariff} />
        <ShiftBreakdown title="По столам" rows={byTable} />
      </div>

      <SectionHead title="Сессии" meta={`${count(sessions.length)} шт.`} />
      {sessions.length === 0 ? (
        <EmptyState message="В смене нет завершённых сессий." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Стол</th>
                <th>Тариф</th>
                <th>Начало</th>
                <th className="num">Длит.</th>
                <th className="num">Сумма</th>
                <th>Оплата</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>{s.tableName}</td>
                  <td>{s.tariffName}</td>
                  <td>{dateTime(s.startedAt)}</td>
                  <td className="num">{duration(s.durationMin)}</td>
                  <td className="num">
                    {money(s.amount)} <Cur />
                  </td>
                  <td>{paymentLabel(s.paymentMethod)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function ShiftBreakdown({
  title,
  rows
}: {
  title: string
  rows: CloudShiftDetail['byTariff']
}): React.JSX.Element {
  const total = rows.reduce((a, r) => a + r.amount, 0)
  return (
    <div>
      <SectionHead title={title} meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет данных." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Наименование</th>
                <th className="num">Кол-во</th>
                <th className="num">Сумма</th>
                <th className="num">Доля</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="num">{count(r.count)}</td>
                  <td className="num">
                    {money(r.amount)} <Cur />
                  </td>
                  <td className="num">{share(r.amount, total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function KV({ k, v }: { k: string; v: ReactNode }): React.JSX.Element {
  return (
    <div className="kv">
      <span className="kv__k">{k}</span>
      <span className="kv__v">{v}</span>
    </div>
  )
}
